import React, {
  useState,
  useEffect,
  useCallback,
  PropsWithChildren,
} from 'react';
import { addErrorHandler, removeErrorHandler, AppError } from 'single-spa';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { Typography, Button, Flex } from '@robinpowered/ui-kit';
import { Sentry } from 'lib/sentry';
import { config } from 'config';

type MicroFrontendError = {
  appName: string;
  error: Error;
};

export const MicroFrontendErrorBoundary = ({
  children,
}: PropsWithChildren): JSX.Element => {
  const { t } = useTranslation('common');
  const [loadError, setLoadError] = useState<MicroFrontendError | null>(null);

  const handleError = useCallback((error: AppError) => {
    // Only handle loading errors (LOAD_ERROR, SKIP_BECAUSE_BROKEN)
    // These occur when the bundle fails to load (network error, 404, etc.)
    if (
      error.appOrParcelName &&
      (error.message?.toLowerCase().includes('loading') ||
        error.message?.toLowerCase().includes('load') ||
        error.message?.toLowerCase().includes('failed to fetch') ||
        error.message?.toLowerCase().includes('networkerror') ||
        error.message?.toLowerCase().includes('chunkloaderror'))
    ) {
      setLoadError({
        appName: error.appOrParcelName,
        error: error,
      });

      // Report to Sentry in non-development environments
      if (config.robinEnv !== 'development') {
        Sentry.captureException(error, {
          tags: {
            appName: error.appOrParcelName,
            errorType: 'micro-frontend-load-error',
          },
        });
      }
    }
  }, []);

  useEffect(() => {
    addErrorHandler(handleError);

    return () => {
      removeErrorHandler(handleError);
    };
  }, [handleError]);

  const handleRetry = useCallback(() => {
    setLoadError(null);

    window.location.reload();
  }, []);

  if (loadError) {
    return (
      <ErrorContainer align="center" justify="center" vertical>
        <Typography.Title level={2}>
          {t('errors.app_load_error_heading')}
        </Typography.Title>
        <ErrorMessage>{t('errors.app_load_error_message')}</ErrorMessage>
        <Button type="primary" onClick={handleRetry}>
          {t('errors.retry_button')}
        </Button>
      </ErrorContainer>
    );
  }

  return <>{children}</>;
};

const ErrorContainer = styled(Flex)`
  height: 100%;
  width: 100%;
  padding: 48px 0 96px;
  text-align: center;
`;

const ErrorMessage = styled(Typography.Text)`
  margin: 0 0 24px;
  color: #666;
  max-width: 400px;
`;
